// WHICH PREPROCESSOR AUTHORS A QUESTION, and the debug-only affordance for changing it.
//
// The contract server registers its authoring strategies ("variants") in reviewed code
// and dispatches `POST /preprocess/auto` strictly by `variant_id` — so the widest thing
// this hook can do is NAME one of the strategies the server already advertises. It never
// sees or sends a prompt; there is no field it could put one in.
//
// TWO INDEPENDENT GATES on the picker, exactly the shape useCopyLinksGate.js uses:
//
//   1. `allowed` — the server's own answer, from /api/me's `debugUIEnabled`
//      (server/flags/debugUi.js): the EXPOSE_DEBUG_UI hard master switch, unset in
//      partner-facing prod, ANDed with the `chatboard-debug-ui` GOFF flag. THIS IS A
//      MIRROR, NOT THE GATE. The same decision is re-made server-side per request:
//      `resolvePreprocessVariantGate` in server.js refuses a `variant_id` a deployment is
//      not allowed to honour, and `GET /api/forecast/preprocess/variants` 404s there. So
//      a browser that forges the field changes nothing — hiding a control is not a gate,
//      and this exists only so we never offer one the API will refuse.
//   2. `debugUI` — the per-browser Debug reveal (the triple-click on the SETTINGS header,
//      persisted as `sooth-debugUI`), which is available on every deployment including
//      prod and is therefore NOT a non-production gate on its own. Gate 1 is what makes
//      this control integration/staging-only.
//
// Strict `=== true` on the flag, so a missing `debugUIEnabled` (an older /api/me, a failed
// flag eval, a 401 body) fails CLOSED — matching the server, where a missing flag, an
// unreachable relay and a throwing evaluator all mean off.
//
// THE SELECTION IS SESSION-ONLY — deliberately NOT persisted, which is where this parts
// company with the copy-links reveal. That reveal is a door: it changes what you can see.
// This changes what the pipeline RUNS, and a non-default authoring strategy is
// indistinguishable on the wire from the default once the contract comes back. A forgotten
// persisted override would therefore quietly attribute every later forecast in that
// browser to a strategy nobody remembered choosing — which is the exact failure the
// variant registry exists to prevent, arriving through the front door. Reloading is the
// escape hatch, and it should stay one.
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Own the debug preprocess-variant picker: its gate, the advertised strategies, and the
 * per-session selection threaded into the submit path.
 *
 * Lives in a hook rather than as `useState` calls in `AppInner` because the state
 * architecture ratchet checks `appInnerUseState` strict-exact, and a debug affordance
 * should not buy itself slots in the orchestrator. See chatboard/CLAUDE.md → State
 * architecture, and useCopyLinksGate.js for the same reasoning spelled out.
 *
 * @param {object} args
 * @param {boolean} args.debugUIEnabled Deployment answer from /api/me (`debugUIEnabled`).
 * @param {boolean} args.debugUI        The per-browser Debug reveal.
 * @param {string}  [args.deploymentDefault] /api/me's `preprocessVariant` — this
 *   deployment's flag value, or "" for the contract server's registry default. Used only
 *   to LABEL the default entry before the listing fetch resolves.
 * @param {() => Promise<{variants: Array<object>, deploymentDefault: string}|null>} args.fetchVariants
 *   The API-seam reader (`services/forecastApi.getPreprocessVariants`). Taken as the bare
 *   function rather than an `{api}` bag so its identity is stable across renders — the
 *   fetch effect lists it as a dep, and a per-render object literal would re-arm the
 *   effect on every render.
 * @returns {{
 *   available: boolean,
 *   variantId: string,
 *   setVariantId: Function,
 *   clear: Function,
 *   variants: Array<object>,
 *   deploymentDefault: string,
 *   loading: boolean,
 * }} `variantId` is "" when nothing is overridden — pass it straight to
 *   `api.preprocess`, which omits the field for "".
 */
export function usePreprocessVariant({
  debugUIEnabled,
  debugUI,
  deploymentDefault = "",
  fetchVariants,
}) {
  const available = debugUIEnabled === true && debugUI === true;
  // The chosen strategy for THIS session. "" = no override.
  const [selected, setSelected] = useState("");
  // `null` = never loaded; `[]` = loaded and there is nothing to offer (a failed or
  // refused fetch). The two must be distinguishable, because `loading` is DERIVED from
  // this rather than held as its own state — `react-hooks/set-state-in-effect` (an eslint
  // ERROR here, and the baseline is zero) forbids the synchronous `setLoading(true)` that
  // a separate loading flag would need in the effect body.
  const [variants, setVariants] = useState(null);
  // Starts as /api/me's value and is replaced by the listing's `deployment_default` once
  // fetched. The two agree; the listing is just the fresher read, and having a value
  // before the fetch is what keeps the picker from flashing an unlabelled default.
  const [fetchedDefault, setFetchedDefault] = useState("");
  // In-flight marker in a REF, not state, for the same reason as above: it must be set
  // synchronously inside the effect, and a ref write is not a render.
  const inFlightRef = useRef(false);

  // Fetch the advertised strategies once the picker becomes available. Gated on
  // `available` rather than on mount so a deployment without a debug surface never issues
  // the request at all (it would 404 there — the route is gated too).
  useEffect(() => {
    if (!available || variants !== null || inFlightRef.current) return;
    inFlightRef.current = true;
    let live = true;
    fetchVariants()
      .then((res) => {
        if (!live) return;
        // `null` covers both "this deployment has no debug surface" (404) and a real
        // failure; neither is worth an error state — the row says it could not load and
        // the dropdown stays disabled.
        setVariants(res ? res.variants : []);
        if (res) setFetchedDefault(res.deploymentDefault);
      })
      .catch(() => {
        if (live) setVariants([]);
      })
      .finally(() => {
        inFlightRef.current = false;
      });
    return () => {
      live = false;
    };
  }, [available, variants, fetchVariants]);

  const clear = useCallback(() => setSelected(""), []);

  return {
    available,
    // DERIVED, not cleared by an effect: the selection is forced back to "" the instant
    // the gate shuts (the flag was flipped off, or Debug was hidden), so a stale choice
    // cannot outlive the surface that set it and keep steering preprocess. Deriving it
    // rather than writing state in an effect also means there is no render in between
    // during which a submit could still read the old value.
    variantId: available ? selected : "",
    setVariantId: setSelected,
    clear,
    variants: variants || [],
    deploymentDefault: fetchedDefault || deploymentDefault,
    loading: available && variants === null,
  };
}
